/* TODO:
Currently, the angular cli does provide a way to add entry points other then `polyfills.ts`, this is why we use the
polyfills to run `pre-load.ts` and the actual polyfills are set here.

This is the best solution now, without hacking the cli.
`pre-load.ts` is very small, and loads the `oidc` client using async, because oidc has most polyfills inside of it,
this import of the polyfills is not big.

Track GH issue: https://github.com/angular/angular-cli/issues/9905
 */
import { enableProdMode } from '@angular/core';
import { platformBrowser } from '@angular/platform-browser';
import { disposeView } from '@sg/pre-app-init';
import 'reflect-metadata';
import { register } from 'swiper/element/bundle';
import { brandingStyleLoader, currentApp, getDeployUrl, identityReady, showDefaultError } from './app-bootstrap';
import { AppModule } from './app/app.module';
import {
  DEMO_MSW_RELOAD_GUARD,
  DEMO_TEARDOWN_RELOAD_GUARD,
  isPageControlled,
  isServiceWorkerSupported,
  MOCK_ACCESS_TOKEN,
  MOCK_SW_SCRIPT_MARKER,
} from './demo-sw-sentinels';
import { envApi } from './environments/environment';
import './polyfills';

function isBustingBusterSafe() {
  if (self === top) {
    return true;
  }
  // In general, only bust when the top is not sharegain or localhost (for development)
  if (top.location.hostname === 'localhost' || top.location.hostname.match(/^.+\.sharegain.com$/)) {
    return true;
  }
  return false;
}

if (!isBustingBusterSafe()) {
  throw new Error(`Invalid environment`);
}

const { production, enableMocking } = envApi.environment;
if (production) {
  enableProdMode();
}

/**
 * Start the application
 * If mocking is enabled, start MSW before Angular bootstraps
 */
async function startApp() {
  // Register Swiper web components
  register();

  // Start MSW if mocking is enabled.
  // NOTE: this imports the mock-bootstrap *seam*, not ./mocks/browser directly.
  // The default mock-bootstrap is a no-op that pulls in zero mock code, so clean
  // builds (ci/test/production) bundle no MSW/handlers/demo data. Demo-capable
  // builds fileReplace it with mock-bootstrap.demo (see angular.json).
  if (enableMocking) {
    const { startMockWorker } = await import('./mocks/mock-bootstrap');
    await startMockWorker();
    // startMockWorker() already waits for the worker to take control of the page
    // (see browser.ts) so early bootstrap requests are intercepted. Fallback: if
    // control still hasn't arrived (rare — the worker never claimed this client),
    // reload once so the reloaded page loads already under SW control. Without
    // this the first ?demo= load's earliest calls (user/info, signalr negotiate)
    // bypass MSW to the real backend and 401 (WI 65929). Guarded against loops.
    if (isServiceWorkerSupported() && !isPageControlled() && typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem(DEMO_MSW_RELOAD_GUARD) !== '1') {
        sessionStorage.setItem(DEMO_MSW_RELOAD_GUARD, '1');
        location.reload();
        return;
      }
    } else if (typeof sessionStorage !== 'undefined' && isPageControlled()) {
      // Controlling now — clear the one-shot so a later fresh registration can
      // self-heal too.
      sessionStorage.removeItem(DEMO_MSW_RELOAD_GUARD);
    }
  } else if (await teardownStaleDemoIfAny()) {
    // WI 65929: a lingering demo service worker was found and unregistered. A SW
    // keeps controlling the current page until the next navigation, so reload
    // once to get a clean, non-mocked page that talks to the real backend
    // directly. The reload re-enters startApp with the stale demo state gone, so
    // teardownStaleDemoIfAny() returns false next time — no reload loop.
    location.reload();
    return;
  }

  // Existing auth and bootstrap flow
  identityReady(envApi.environment)
    .then((user) => {
      const appConfig = currentApp;
      if (appConfig) {
        brandingStyleLoader(appConfig.branding.css, production, getDeployUrl('/'));
      }
      return user;
    })
    .then((user) => {
      if (user) {
        platformBrowser()
          .bootstrapModule(AppModule)
          .then(() => {
            disposeView();
          })
          .catch((err) => {
            showDefaultError(err);
            console.error('main err', err);
          }); // in case of a bootstrapping error, show it.
      }
    })
    .catch((err) => {
      // Error in identityReady chain
    });
}

startApp();

/**
 * WI 65929: on a demo-capable bundle (the sales / demo image) loaded WITHOUT an
 * active demo, a `mockServiceWorker.js` registered by a PRIOR demo session keeps
 * controlling the page and forwards requests — carrying the demo's stale
 * mock-auth token (`access_token = 'mock-access-token'`, set by the demo
 * auth-bypass) — to the REAL backend (api-sales), which rejects them in a
 * 401/404 flood (mock legal entity LE-SUPER-510 not found).
 *
 * When mocking is NOT being started this load, proactively clear that stale demo
 * state so the app talks to the real backend cleanly (or does real auth):
 *  - clear the demo auth-bypass sentinels (only when the mock token is present —
 *    never touch a real token);
 *  - unregister any lingering MSW service worker (identified by its script URL —
 *    real/PWA service workers are left untouched).
 *
 * Returns true if a lingering MSW worker was actually unregistered, signalling
 * the caller to reload (a SW keeps controlling the page until the next
 * navigation). Best-effort: never throws, never blocks bootstrap. On clean
 * ci/test/prod builds this is a no-op (no MSW worker, no mock token).
 *
 * In practice this only ever runs on the SALES image (`environment.sales.ts`,
 * `enableMocking: isDemoMode()`); the DEMO image hard-codes `enableMocking: true`
 * so this else-branch never runs there — its non-demo welcome state is instead
 * covered by the MSW `onUnhandledRequest` block (browser.ts).
 *
 * Reload safety: a reload is signalled only when a worker was genuinely removed
 * AND we have not already reloaded once this tab session (a `sessionStorage`
 * one-shot guard). This is a hard backstop so that if `unregister()` ever
 * persistently fails or no-ops, we can never enter an infinite reload loop. The
 * guard is reset once the page reaches a clean state (no MSW worker) so a later
 * demo→exit cycle in the same session can reload again.
 *
 * Note (accepted): the MSW worker is origin-scoped, so tearing it down here also
 * affects another tab mid-demo on the same origin — that tab keeps working until
 * its next navigation. Narrow, and reps run one demo at a time.
 */
async function teardownStaleDemoIfAny(): Promise<boolean> {
  const RELOAD_GUARD = DEMO_TEARDOWN_RELOAD_GUARD;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('access_token') === MOCK_ACCESS_TOKEN) {
      ['access_token', 'id_token', 'refresh_token', 'user_authenticated'].forEach((k) => localStorage.removeItem(k));
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('authenticated');
      }
    }
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return false;
    }
    const registrations = await navigator.serviceWorker.getRegistrations();
    const mswRegistrations = registrations.filter((reg) =>
      [reg.active, reg.installing, reg.waiting].some((w) => (w?.scriptURL ?? '').includes(MOCK_SW_SCRIPT_MARKER))
    );
    if (mswRegistrations.length === 0) {
      // Clean state — reset the one-shot guard so a future demo→exit can reload.
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(RELOAD_GUARD);
      }
      return false;
    }
    // `unregister()` resolves true only when a registration was actually removed.
    const results = await Promise.all(mswRegistrations.map((reg) => reg.unregister().catch(() => false)));
    const removedAny = results.some((ok) => ok === true);
    if (!removedAny) {
      // Nothing was actually removed — do NOT reload (avoids a loop on a
      // persistently-failing unregister). NOTE: this is the non-mocking load, so
      // MSW was never started this time and onUnhandledRequest guards nothing here;
      // a still-controlling stale worker keeps forwarding until the next
      // navigation. Accepted as bounded (unregister virtually never fails); the
      // reload on the success path is the real remedy.
      return false;
    }
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem(RELOAD_GUARD) === '1') {
        return false; // already reloaded once this session — never loop
      }
      sessionStorage.setItem(RELOAD_GUARD, '1');
    }
    return true;
  } catch {
    // A failed teardown must never block the app from booting.
    return false;
  }
}

// `identityReady` doesn't reject, and anyway we don't care about what happens within it, it will display the errors
// that concern identity init.
